<script setup lang="ts">
/**
 * Les gestes de la fiche d'une surveillance (`rendreFiche` S:5572-5589) :
 * désactiver ou réactiver, changer les destinataires, modifier les
 * paramètres, écrire la consigne, acquitter l'alarme en cours, m'inscrire ou
 * me retirer (gestes 4, 10 à 15).
 *
 * Ce que l'utilisateur doit vivre : chaque geste se montre actif, inactif
 * avec sa raison, ou pas du tout, selon la matrice ; il ouvre un dialogue qui
 * dit ce qu'il fait ; rien ne bascule à l'écran avant la réponse, puis la
 * surveillance est relue. Un refus se dit par sa phrase, même sur un bouton
 * resté actif ; une écriture dont on ne sait pas si elle est passée se dit,
 * et aucun geste ne repart avant la relecture. Un corps vide ne part jamais.
 */
// ================================
// IMPORTS
// ================================
import { computed, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useAuthStore } from "@core/stores/auth.store"
import { useAcces } from "@modules/alarmes-v2/acces/useAcces"
import {
    cibleSurveillance,
    useActionsStore,
    type GesteEcriture,
} from "@modules/alarmes-v2/stores/actions.store"
import type { StatutMembres } from "@modules/alarmes-v2/stores/membres.store"
import { serviceAlarmesV2 } from "@modules/alarmes-v2/services/alarmes-v2.service"
import type {
    AlarmesV2Alarme,
    AlarmesV2Membre,
    AlarmesV2Modification,
    AlarmesV2NombreDuType,
    AlarmesV2Personne,
    AlarmesV2Surveillance,
    AlarmesV2SurveillanceEcrite,
} from "@modules/alarmes-v2/types/alarmes-v2"
import {
    destinatairesEcrits,
    destinatairesPourMoi,
    phraseDuRefus,
} from "@modules/alarmes-v2/utils/ecritures"
import type { GesteDeRangee } from "./RangeeGestes.vue"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n()

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        surveillance: AlarmesV2Surveillance
        alarmeOuverte?: AlarmesV2Alarme | null
        personnes?: Record<string, AlarmesV2Personne>
        /** Les nombres du type, pour nommer les seuils. */
        nombres?: AlarmesV2NombreDuType[]
        /** L'équipement et le type, sous le titre du dialogue des paramètres. */
        contexte?: string
        membres?: Record<string, AlarmesV2Membre>
        statutMembres?: StatutMembres
        /** Relit la surveillance ; vrai si la relecture a abouti. */
        relire: () => Promise<boolean>
    }>(),
    {
        alarmeOuverte: null,
        personnes: () => ({}),
        nombres: () => [],
        contexte: "",
        membres: () => ({}),
        statutMembres: "attente",
    }
)

const auth = useAuthStore()
const actions = useActionsStore()
const { acces } = useAcces()

/** Le dialogue ouvert. */
type Dialogue =
    | "desactiver"
    | "reactiver"
    | "parametres"
    | "destinataires"
    | "consigne"
    | "retrait"
const dialogue = ref<Dialogue | null>(null)

/** La dernière liste envoyée était vide (« Plus personne n'est prévenu »). */
const listeVideEnvoyee = ref(false)

// ================================
// COMPUTED
// ================================
const getCible = computed(() => cibleSurveillance(props.surveillance._id))
const getEtat = computed(() => actions.etat(getCible.value))
const getOccupee = computed(() => actions.occupee(getCible.value))

const getDesactivee = computed(() => props.surveillance.etat === "desactivee")

/** La rangée des deux gestes d'état et de destinataires (S:5573-5576). */
const getRangee = computed((): GesteDeRangee[] => [
    getDesactivee.value
        ? {
              acces: acces("fiche_remettre_en_service"),
              libelle: t("alarmesV2.actions.reactiver"),
              classe: "rounded border border-primary-600 bg-primary-600 px-3 py-1.5 text-sm font-medium text-white",
          }
        : {
              acces: acces("fiche_mettre_hors_service"),
              libelle: t("alarmesV2.actions.desactiver"),
              classe: "rounded border border-red-700 bg-red-700 px-3 py-1.5 text-sm font-medium text-white",
          },
    {
        acces: acces("fiche_changer_destinataires"),
        libelle: t("alarmesV2.actions.changerDestinataires"),
    },
])

/** Les destinataires servis, lisibles en entier, ou `null`. */
const getDestinatairesEcrits = computed(() =>
    Array.isArray(props.surveillance.destinataires)
        ? destinatairesEcrits(props.surveillance.destinataires)
        : null
)

/** Vrai, faux, ou `null` (non servi) : la personne est-elle prévenue ? */
const getInscrit = computed((): boolean | null => {
    const d = getDestinatairesEcrits.value
    if (!d || !auth.userId) return null
    return d.some((x) => x._id_user === auth.userId)
})

/** La consigne servie : son texte, `null` sans consigne, `undefined` non servie. */
const getConsigne = computed((): string | null | undefined => {
    const c = props.surveillance.consigne
    if (c === undefined) return undefined
    return c?.texte ? c.texte : null
})

/** La phrase d'un refus pour le dialogue ouvert, `""` sinon. */
const getRefus = computed((): string => {
    const e = getEtat.value
    if (!e || e.statut !== "refus" || !e.erreur) return ""
    const p = phraseDuRefus(e.erreur)
    return p.texte ?? t(p.cle as string)
})

/** Ce que dit le succès du dernier geste. */
const getTexteFait = computed((): string => {
    const e = getEtat.value
    if (!e || e.statut !== "fait") return ""
    if (e.geste === "destinataires" || e.geste === "retirer") {
        const r = (e.valeur ?? {}) as AlarmesV2SurveillanceEcrite
        if (listeVideEnvoyee.value || r.etat_motif === "aucun_destinataire")
            return t("alarmesV2.actions.fait.aucunDestinataire")
    }
    const cles: Partial<Record<GesteEcriture, string>> = {
        desactiver: "alarmesV2.actions.fait.desactiver",
        reactiver: "alarmesV2.actions.fait.reactiver",
        parametres: "alarmesV2.actions.fait.parametres",
        destinataires: "alarmesV2.actions.fait.destinataires",
        inscrire: "alarmesV2.actions.fait.inscrire",
        retirer: "alarmesV2.actions.fait.retirer",
        consigne: "alarmesV2.actions.fait.consigne",
    }
    const cle = cles[e.geste]
    return cle ? t(cle) : ""
})

/** L'annonce ne vaut que pour les gestes de la fiche (pas l'acquittement). */
const getAAnnonce = computed(() => !!getEtat.value)

// ================================
// HANDLERS
// ================================
/** Ouvre un dialogue ; l'issue précédente s'efface (sauf pendant une écriture). */
function ouvrir(d: Dialogue): void {
    if (getOccupee.value) return
    actions.oublier(getCible.value)
    dialogue.value = d
}

function onRangee(cle: string): void {
    if (cle === "fiche_remettre_en_service") ouvrir("reactiver")
    else if (cle === "fiche_mettre_hors_service") ouvrir("desactiver")
    else if (cle === "fiche_changer_destinataires") ouvrir("destinataires")
}

function onFermer(): void {
    if (getOccupee.value) return
    dialogue.value = null
}

/**
 * Envoie un corps de la route 5 : UNE requête, jamais un corps vide, puis la
 * relecture. Le dialogue se ferme sur un succès ou une incertitude ; il reste
 * ouvert sur un refus, qui se dit dedans.
 */
async function ecrire(
    geste: GesteEcriture,
    corps: AlarmesV2Modification
): Promise<void> {
    if (Object.keys(corps).length === 0) return
    const id = props.surveillance._id
    listeVideEnvoyee.value =
        Array.isArray(corps.destinataires) && corps.destinataires.length === 0
    const issue = await actions.ecrire({
        cible: cibleSurveillance(id),
        geste,
        appel: () => serviceAlarmesV2.alarmesV2ModifierSurveillance(id, corps),
        relire: props.relire,
    })
    if (issue.issue === "refus" || issue.issue === "ignoree") return
    dialogue.value = null
}

function onValiderEtat(corps: AlarmesV2Modification): void {
    void ecrire(
        dialogue.value === "reactiver" ? "reactiver" : "desactiver",
        corps
    )
}
function onValiderParametres(corps: AlarmesV2Modification): void {
    void ecrire("parametres", corps)
}
function onValiderDestinataires(corps: AlarmesV2Modification): void {
    void ecrire("destinataires", corps)
}
function onValiderConsigne(corps: AlarmesV2Modification): void {
    void ecrire("consigne", corps)
}

/** M'inscrire ou me retirer ; me retirer en dernier demande une confirmation. */
function onMoi(confirme = false): void {
    if (getOccupee.value) return
    const d = props.surveillance.destinataires
    const inscrire = getInscrit.value === false
    if (!Array.isArray(d) || getInscrit.value === null) return
    const liste = destinatairesPourMoi(d, auth.userId, inscrire)
    if (!liste) return
    if (!inscrire && liste.length === 0 && !confirme) {
        actions.oublier(getCible.value)
        dialogue.value = "retrait"
        return
    }
    void ecrire(inscrire ? "inscrire" : "retirer", { destinataires: liste })
}
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <section class="flex flex-col gap-2" data-test="gestes-fiche">
        <div class="flex flex-wrap items-start gap-2">
            <RangeeGestes :gestes="getRangee" @geste="onRangee" />
            <BoutonGeste
                :acces="acces('fiche_modifier_parametres')"
                :libelle="t('alarmesV2.actions.modifierParametres')"
                @click="ouvrir('parametres')"
            />
            <BoutonGeste
                v-if="getConsigne !== undefined"
                :acces="acces('consigne_ecrire')"
                :libelle="
                    getConsigne
                        ? t('alarmesV2.actions.modifierConsigne')
                        : t('alarmesV2.actions.ecrireConsigne')
                "
                @click="ouvrir('consigne')"
            />
            <BoutonAcquitter
                :alarme="alarmeOuverte"
                :surveillance="surveillance"
                :relire="relire"
            />
            <BoutonGeste
                v-if="getInscrit !== null"
                :acces="acces('fiche_s_inscrire_se_retirer')"
                :libelle="
                    getInscrit
                        ? t('alarmesV2.actions.retirer')
                        : t('alarmesV2.actions.inscrire')
                "
                classe="rounded px-3 py-1.5 text-sm font-medium text-primary-700 underline"
                @click="onMoi()"
            />
        </div>
        <ul
            class="list-disc pl-5 text-xs text-surface-600"
            data-test="aide-gestes"
        >
            <li>{{ t("alarmesV2.actions.aide.desactiver") }}</li>
            <li>{{ t("alarmesV2.actions.aide.reactiver") }}</li>
            <li>{{ t("alarmesV2.actions.aide.parametres") }}</li>
            <li>{{ t("alarmesV2.actions.aide.destinataires") }}</li>
        </ul>
        <AnnonceEcriture
            v-if="getAAnnonce"
            :cible="getCible"
            :texte-fait="getTexteFait"
            :relire="relire"
        />

        <!-- ================================ -->
        <!-- DIALOGUES -->
        <!-- ================================ -->
        <DialogueDesactiver
            v-if="dialogue === 'desactiver' || dialogue === 'reactiver'"
            :mode="dialogue"
            :en-cours="getOccupee"
            :refus="getRefus"
            @valider="onValiderEtat"
            @fermer="onFermer"
        />
        <DialogueParametres
            v-else-if="dialogue === 'parametres'"
            :valeurs="surveillance.valeurs_nombres"
            :nombres="nombres"
            :contexte="contexte"
            :en-cours="getOccupee"
            :refus="getRefus"
            @valider="onValiderParametres"
            @fermer="onFermer"
        />
        <DialogueDestinataires
            v-else-if="
                dialogue === 'destinataires' &&
                Array.isArray(surveillance.destinataires)
            "
            :destinataires="surveillance.destinataires ?? []"
            :membres="membres"
            :statut-membres="statutMembres"
            :personnes="personnes"
            :en-cours="getOccupee"
            :refus="getRefus"
            @valider="onValiderDestinataires"
            @fermer="onFermer"
        />
        <DialogueConsigne
            v-else-if="dialogue === 'consigne' && getConsigne !== undefined"
            :texte="getConsigne"
            :en-cours="getOccupee"
            :refus="getRefus"
            @valider="onValiderConsigne"
            @fermer="onFermer"
        />
        <CadreDialogue
            v-else-if="dialogue === 'retrait'"
            :titre="t('alarmesV2.actions.retirer')"
            :bloque="getOccupee"
            @fermer="onFermer"
        >
            <p
                class="rounded border border-red-200 bg-red-50 p-2 text-sm text-red-800"
                data-test="retrait-dernier"
            >
                {{ t("alarmesV2.actions.inscription.dernier") }}
            </p>
            <p v-if="getOccupee" class="text-sm text-surface-700" role="status">
                {{ t("alarmesV2.actions.envoi") }}
            </p>
            <p
                v-else-if="getRefus"
                class="text-sm text-red-800"
                role="alert"
                data-test="refus"
            >
                {{ getRefus }}
            </p>
            <template #pied>
                <button
                    type="button"
                    class="rounded border border-surface-300 px-3 py-1.5 text-sm"
                    data-focus-initial
                    data-test="annuler"
                    @click="onFermer"
                >
                    {{ t("alarmesV2.actions.annuler") }}
                </button>
                <button
                    type="button"
                    class="rounded border border-red-700 bg-red-700 px-3 py-1.5 text-sm font-medium text-white"
                    :aria-disabled="getOccupee ? 'true' : undefined"
                    data-test="confirmer-retrait"
                    @click="onMoi(true)"
                >
                    {{ t("alarmesV2.actions.inscription.confirmerRetrait") }}
                </button>
            </template>
        </CadreDialogue>
    </section>
</template>
